import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { TrendingUp, Trophy, Music2 } from 'lucide-react';

const PERIODS = [
  { key: 'week', label: 'Cette semaine' },
  { key: 'month', label: 'Ce mois' },
  { key: 'all', label: 'Tous les temps' },
];

export default function TopSongs() {
  const [period, setPeriod] = useState('month');
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data } = await api.get(`/songs/top?period=${period}&limit=20`);
      setRanking(data.ranking || []);
      setLoading(false);
    })();
  }, [period]);

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-4xl">
        <div className="mb-8 rise">
          <div className="overline text-orange-400 flex items-center gap-1.5"><TrendingUp size={12} /> Classement</div>
          <h1 className="font-display text-5xl tracking-tight">Top 20 des chansons</h1>
          <p className="text-stone-400 mt-2 max-w-xl">
            Les morceaux qui font vibrer la communauté — classés par apprenants uniques + complétions (poids ×2).
          </p>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          {PERIODS.map(p => (
            <button
              key={p.key}
              data-testid={`top-period-${p.key}`}
              onClick={() => setPeriod(p.key)}
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                period === p.key
                  ? 'bg-orange-700 text-white border-orange-700'
                  : 'bg-stone-900 text-stone-400 border-stone-800 hover:border-stone-700'
              }`}
            >{p.label}</button>
          ))}
        </div>

        {loading ? (
          <div className="text-stone-500 text-sm">Chargement...</div>
        ) : ranking.length === 0 ? (
          <div className="card-stone p-10 text-center">
            <Music2 className="mx-auto text-orange-400 mb-3" size={40} />
            <div className="font-display text-2xl mb-2">Pas encore de classement</div>
            <p className="text-stone-400">Aucun apprentissage enregistré sur cette période. Va découvrir une chanson pour lancer le compteur !</p>
          </div>
        ) : (
          <div className="space-y-2">
            {ranking.map((r, i) => (
              <Link
                to={`/song/${r.song.id}`}
                key={r.song.id}
                data-testid={`top-song-${i + 1}`}
                className="card-stone p-3 flex items-center gap-4 hover:border-orange-800/40 transition-colors group"
              >
                <div className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center font-display text-2xl ${
                  i === 0 ? 'bg-gradient-to-br from-orange-500 to-orange-700 text-white shadow-lg shadow-orange-900/40'
                  : i < 3 ? 'bg-orange-950/70 text-orange-200 border border-orange-800/50'
                  : 'bg-stone-800 text-stone-300'
                }`}>
                  {i < 3 ? <Trophy size={20} /> : i + 1}
                </div>
                <div className="w-14 h-14 rounded-lg bg-stone-800 overflow-hidden shrink-0">
                  {r.song.cover_url && <img src={r.song.cover_url} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg truncate group-hover:text-orange-300 transition-colors">{r.song.title}</div>
                  <div className="text-stone-400 text-sm truncate">{r.song.artist} · {r.song.genre} · {r.song.difficulty}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-2xl text-orange-300">{r.score}</div>
                  <div className="text-[10px] text-stone-500">{r.unique_learners} apprenant·es · {r.completions} 🏁</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
